/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFireObject } from '@angular/fire/compat/database/interfaces';
import { from, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ITicketAttachmentList, ITicketAttachments } from 'src/app/app-data/models/Attachment';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';

import firebase from 'firebase/compat/app';

@Injectable({
	providedIn: 'root',
})
export class FileAttachCloneService {
	private readonly TICKET_ATTACHMENTS = 'ticketAttachments';

	constructor(private angularFireDatabase: AngularFireDatabase) {}

	/**
	 * Copy attachments to a new ticket, this supports ticket copy across plans
	 * It copies /ticketAttachments nodes to new ticket with new ids
	 * @param projectId project id
	 * @param planId plan id
	 * @param ticketId ticket id
	 */
	public copyAttachment$(
		projectId: string,
		planId: string,
		ticketId: string,
		data: ITicketAttachments
	): Observable<void[]> {
		const promises = [];
		const ticketAttachmentPath = fancyFirebaseGetters.getTicketAttachmentsPath(projectId, planId, ticketId);
		const ticketAttachmentRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			ticketAttachmentPath,
			projectId
		);

		if (!data) {
			return of([]);
		}

		// create /ticketAttachment
		promises.push(ticketAttachmentRef.update(data));
		for (const value of Object.values(data)) {
			// create /projectFiles refs
			promises.push(this.createProjectFilesRefs(projectId, planId, ticketId, value.fileId));

			if (value.thumbnailFileId) {
				promises.push(this.createProjectFilesRefs(projectId, planId, ticketId, value.thumbnailFileId));
			}

			if (value.previewFileId) {
				promises.push(this.createProjectFilesRefs(projectId, planId, ticketId, value.previewFileId));
			}
		}
		return from(Promise.all(promises)).pipe(take(1));
	}

	/**
	 * Delete all attachments from a ticket
	 * @param projectId project id
	 * @param planId plan id
	 * @param ticketId ticket id
	 */
	public deleteTicketAttachments(projectId: string, planId: string, ticketId: string): void {
		const dbRef = this.getTicketAttachmentRef(projectId, planId, ticketId);
		dbRef
			.valueChanges()
			.pipe(take(1))
			.subscribe((data: ITicketAttachments) => {
				if (!data) {
					return;
				}

				for (const [key, value] of Object.entries(data)) {
					this.deleteAttachment(
						projectId,
						planId,
						ticketId,
						key,
						value.fileId,
						value.thumbnailFileId,
						value.previewFileId
					);
				}
			});
	}

	/**
	 * This deletes single attachment from a ticket
	 * @param projectId project id
	 * @param planId plan id
	 * @param ticketId ticket id
	 * @param fileId file id
	 * @param thumbnailFileId thumbnail file id
	 * @param previewFileId preview file id
	 */
	public deleteAttachment(
		projectId: string,
		planId: string,
		ticketId: string,
		attachmentId: string,
		fileId: string,
		thumbnailFileId?: string,
		previewFileId?: string
	): void {
		const path = fancyFirebaseGetters.getTicketAttachmentsPath(projectId, planId, ticketId);
		const dbRef = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(path, projectId)
			.child(attachmentId);
		// remove attachments node
		dbRef.remove();

		// remove /projectFiles refs
		this.deleteProjectFilesRefs(projectId, planId, ticketId, fileId);
		if (thumbnailFileId) {
			this.deleteProjectFilesRefs(projectId, planId, ticketId, thumbnailFileId);
		}

		if (previewFileId) {
			this.deleteProjectFilesRefs(projectId, planId, ticketId, previewFileId);
		}
	}

	/**
	 * Get ticket attachment valueChange observable
	 * @param projectId project id
	 * @param planId plan id
	 * @param ticketId ticket id
	 * @returns Observable of ITicketAttachments
	 */
	public getTicketAttachment$(
		projectId: string,
		planId: string,
		ticketId: string
	): Observable<ITicketAttachmentList> {
		const dbRef = this.getTicketAttachmentRef(projectId, planId, ticketId);
		const ob$ = dbRef.valueChanges();
		return ob$.pipe(
			map((data) => {
				return { [ticketId]: data as ITicketAttachments };
			}),
			take(1)
		);
	}

	/**
	 * Get ticket attachments with ticket ids for an specific plan
	 * @param projectId project id
	 * @param planId plan id
	 * @returns Observable of ITicketAttachmentList
	 */
	public getTicketIdsWithAttachments$(projectId: string, planId: string): Observable<ITicketAttachmentList> {
		const path = fancyFirebaseGetters.getTicketAttachmentsPath(projectId, planId);
		const dbRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, projectId);
		return this.angularFireDatabase.object<ITicketAttachmentList>(dbRef).valueChanges();
	}

	private getTicketAttachmentRef(
		projectId: string,
		planId: string,
		ticketId: string
	): AngularFireObject<ITicketAttachments> {
		const path = fancyFirebaseGetters.getTicketAttachmentsPath(projectId, planId, ticketId);
		const dbRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, projectId);
		return this.angularFireDatabase.object(dbRef);
	}

	private createProjectFilesRefs(
		projectId: string,
		planId: string,
		ticketId: string,
		projectFileKey: string
	): Promise<void> {
		const projectFilesRefPath = this.generatePlanTicketPathRef(projectId, planId, ticketId);
		const path = fancyFirebaseGetters.getProjectFilesPath(projectId);
		const dbRef = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(path, projectId)
			.child(this.TICKET_ATTACHMENTS)
			.child(projectFileKey)
			.child('refs')
			.child(projectFilesRefPath);
		return dbRef.set(true);
	}

	private deleteProjectFilesRefs(
		projectId: string,
		planId: string,
		ticketId: string,
		projectFileKey: string
	): Promise<void> {
		const projectFilesRefPath = this.generatePlanTicketPathRef(projectId, planId, ticketId);
		const path = fancyFirebaseGetters.getProjectFilesPath(projectId);
		const dbRef = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(path, projectId)
			.child(this.TICKET_ATTACHMENTS)
			.child(projectFileKey)
			.child('refs')
			.child(projectFilesRefPath);
		return dbRef.remove();
	}

	// temporary puid until real PUID is implemented
	private generatePlanTicketPathRef(projectId: string, planId: string, ticketId: string): string {
		return `tickets;${projectId};${planId};${ticketId}`;
	}
}
